import React, { useState } from 'react';
import PaymentButton from '../PaymentButton';
import { toast } from 'sonner';
import api from '../../utils/api';
import './SubscriptionPaymentCard.css';

/**
 * SubscriptionPaymentCard
 * Mirrors TermlyFeeCard pattern for platform subscription payments
 * Opens PaymentButton in popup instead of full-page redirect
 */
function SubscriptionPaymentCard({ 
  metrics, 
  subscription,
  onPaymentSuccess
}) {
  const [initiatingPayment, setInitiatingPayment] = useState(false);
  const [subscriptionFee, setSubscriptionFee] = useState(null);
  const [showPaymentButton, setShowPaymentButton] = useState(false);

  if (!metrics || !subscription) {
    return (
      <div className="loading-state">
        <div className="spinner-pulse"></div>
        <p>Loading subscription information...</p>
      </div>
    );
  }

  // Handle payment initiation
  const handlePayNow = async () => {
    try {
      setInitiatingPayment(true);
      
      // Create a subscription fee object similar to student fee structure
      const fee = {
        id: subscription.id,
        fee_name: 'Platform Subscription',
        balance: subscription.total_amount_due - subscription.amount_paid,
        amount_to_pay: subscription.total_amount_due - subscription.amount_paid,
        is_subscription: true,
        subscription_id: subscription.id,
        total_amount: subscription.total_amount_due,
        amount_paid: subscription.amount_paid,
        student_count: metrics.student_count,
        description: `Platform subscription for ${metrics.student_count} students @ GHS ${metrics.unit_price}/student`
      };
      
      setSubscriptionFee(fee);
      setShowPaymentButton(true);
    } catch (error) {
      toast.error('Failed to prepare payment');
      console.error(error);
    } finally {
      setInitiatingPayment(false);
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = () => {
    setSubscriptionFee(null);
    setShowPaymentButton(false);
    toast.success('Payment processed successfully!');
    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
  };

  // Show payment button view (similar to TermlyFeeCard)
  if (showPaymentButton && subscriptionFee) {
    return (
      <div className="subscription-payment-card">
        <div className="payment-processing-view">
          <div className="processing-header">
            <button 
              className="back-btn"
              onClick={() => setShowPaymentButton(false)}
            >
              ← Back to Billing
            </button>
            <h3>Complete Payment</h3>
            <div></div>
          </div>
          
          <div className="payment-details-summary">
            <div className="detail-item">
              <span className="label">Subscription Type:</span>
              <span className="value">{subscriptionFee.fee_name}</span>
            </div>
            <div className="detail-item">
              <span className="label">Outstanding Balance:</span>
              <span className="value">GHS {subscriptionFee.balance.toFixed(2)}</span>
            </div>
            <div className="detail-item">
              <span className="label">Description:</span>
              <span className="value">{subscriptionFee.description}</span>
            </div>
          </div>
          
          <PaymentButton 
            fee={subscriptionFee}
            onPaymentSuccess={handlePaymentSuccess}
            fullWidth={true}
            paymentConfig={{
              endpoint: `/api/billing/subscriptions/${subscription.id}/pay`,
              idField: 'subscription_id',
              resourceId: subscription.id
            }}
          />
        </div>
      </div>
    );
  }

  // Show billing summary view
  return (
    <div className="subscription-payment-card">
      {/* Billing Summary */}
      <div className="billing-summary-section">
        <div className="summary-header">
          <h3>Payment Summary</h3>
        </div>

        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">Total Due:</span>
            <span className="amount">GHS {metrics.total_due.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Amount Paid:</span>
            <span className="amount paid">GHS {metrics.total_paid.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Outstanding:</span>
            <span className="amount outstanding">GHS {metrics.remaining_balance.toFixed(2)}</span>
          </div>
        </div>

        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(metrics.total_paid / metrics.total_due) * 100}%` }}
          ></div>
        </div>
        <div className="progress-label">
          {Math.round((metrics.total_paid / metrics.total_due) * 100)}% Paid
        </div>
      </div>

      {/* Subscription Details */}
      <div className="subscription-details-section">
        <div className="detail-row">
          <span className="label">Active Students:</span>
          <span className="value">{metrics.student_count}</span>
        </div>
        <div className="detail-row">
          <span className="label">Unit Price:</span>
          <span className="value">GHS {metrics.unit_price.toFixed(2)}/student</span>
        </div>
        <div className="detail-row">
          <span className="label">Days Until Due:</span>
          <span className="value">{metrics.days_until_due} days</span>
        </div>
      </div>

      {/* Action Buttons */}
      {metrics.remaining_balance > 0 && (
        <button
          className="btn-pay-subscription"
          onClick={handlePayNow}
          disabled={initiatingPayment}
        >
          {initiatingPayment ? 'Preparing Payment...' : 'Pay Now'}
        </button>
      )}
    </div>
  );
}

export default SubscriptionPaymentCard;
